/**
 * Кадр телефона для Chef Master: `{type: '…ChefMasterData', gameState, hands?}`.
 * Руки — до двух, по 13 суставов (контракт рук веб-игр), координаты 0…1 в виде 16:9,
 * зеркально, начало слева сверху. Рука без любого сустава отбрасывается.
 */
export type ChefMasterGameState = 'PREPARING' | 'STARTED' | 'PAUSED' | 'GAME_OVER';

export const CHEF_MASTER_JOINTS = [
  'wrist',
  'indexMcp', 'indexPip', 'indexTip',
  'middleMcp', 'middlePip', 'middleTip',
  'ringMcp', 'ringPip', 'ringTip',
  'pinkyMcp', 'pinkyPip', 'pinkyTip',
] as const;

export type ChefMasterHand = Record<(typeof CHEF_MASTER_JOINTS)[number], { x: number; y: number }>;

export interface ChefMasterFrame {
  gameState: ChefMasterGameState;
  /** `null` — поля `hands` в кадре нет (PREPARING и PAUSED); `[]` — рук не видно. */
  hands: ChefMasterHand[] | null;
}

const STATES: readonly unknown[] = ['PREPARING', 'STARTED', 'PAUSED', 'GAME_OVER'];

function parsePoint(value: unknown): { x: number; y: number } | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as Record<string, unknown>;
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

function parseHand(value: unknown): ChefMasterHand | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const joints = CHEF_MASTER_JOINTS.map((joint) => [joint, parsePoint(raw[joint])] as const);
  if (joints.some(([, point]) => point === null)) return null;
  return Object.fromEntries(joints) as ChefMasterHand;
}

export function parseChefMasterFrame(raw: unknown): ChefMasterFrame | null {
  if (!raw || typeof raw !== 'object') return null;
  const { gameState, hands } = raw as Record<string, unknown>;
  if (!STATES.includes(gameState)) return null;
  return {
    gameState: gameState as ChefMasterGameState,
    hands: Array.isArray(hands)
      ? hands.map(parseHand).filter((h): h is ChefMasterHand => h !== null).slice(0, 2)
      : null,
  };
}
